/**
 * Core WebSocket client for OpenClaw Gateway communication.
 *
 * Responsibilities:
 * - WebSocket connection lifecycle (open, close, reconnect)
 * - Auto-reconnect with exponential backoff
 * - Frame send/receive
 * - Request-response correlation (via PendingRequestMap)
 * - Event dispatching to registered listeners
 * - Keepalive tick management
 *
 * This is a transport layer — it does NOT handle authentication.
 * The auth module (handshake.ts) uses this client to perform the
 * connect handshake.
 */

import type {
  RequestFrame,
  ResponseFrame,
  EventFrame,
} from '../types/protocol';
import {
  parseFrame,
  serializeFrame,
  buildRequest,
  isResponseFrame,
  isEventFrame,
  PendingRequestMap,
} from './frames';
import { KeepaliveManager } from './keepalive';
import {
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_MAX_DELAY_MS,
} from '../utils/constants';

// ── Event Types ──

export type WSClientState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'authenticated';

export interface WSClientEvents {
  /** Raw event frame from gateway */
  event: (frame: EventFrame) => void;
  /** Connection state changed */
  stateChange: (state: WSClientState) => void;
  /** WebSocket closed (with close code/reason) */
  close: (code: number, reason: string) => void;
  /** WebSocket error */
  error: (error: Event) => void;
  /** Raw response frame that wasn't matched to a pending request */
  unmatchedResponse: (frame: ResponseFrame) => void;
}

type EventListener<K extends keyof WSClientEvents> = WSClientEvents[K];

// ── Client ──

export interface WSClientOptions {
  /** WebSocket URL (wss://...) */
  url: string;
  /** Whether to auto-reconnect on close (default: true) */
  autoReconnect?: boolean;
  /** Custom reconnect filter — return false to skip reconnect for specific close codes */
  shouldReconnect?: (code: number, reason: string) => boolean;
}

/**
 * WebSocket client for the OpenClaw Gateway protocol.
 *
 * Usage:
 * ```ts
 * const client = new WSClient({ url: 'wss://my-gateway.example.com' });
 * client.on('event', (frame) => { ... });
 * client.on('stateChange', (state) => { ... });
 * client.connect();
 *
 * // Send a request and await the response:
 * const payload = await client.request('chat.history', { sessionKey: '...' });
 * ```
 */
export class WSClient {
  private ws: WebSocket | null = null;
  private options: Required<WSClientOptions>;
  private _state: WSClientState = 'disconnected';
  private retryDelay = RECONNECT_BASE_DELAY_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();
  private pendingRequests = new PendingRequestMap();
  private keepalive: KeepaliveManager;
  private _intentionalClose = false;

  constructor(options: WSClientOptions) {
    this.options = {
      autoReconnect: true,
      shouldReconnect: () => true,
      ...options,
    };

    this.keepalive = new KeepaliveManager(() => {
      console.warn('[ws] Tick watchdog timeout — closing connection');
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(4000, 'tick timeout');
      }
    });
  }

  // ── Public API ──

  /** Current connection state */
  get state(): WSClientState {
    return this._state;
  }

  /** Whether the WebSocket is open */
  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Connect to the gateway */
  connect(): void {
    this.cancelReconnect();
    this._intentionalClose = false;

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try { this.ws.close(); } catch { /* ignore */ }
    }

    this.setState('connecting');

    const url = this.normalizeUrl(this.options.url);
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.retryDelay = RECONNECT_BASE_DELAY_MS;
      this.setState('connected');
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event.data as string);
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.keepalive.stop();
      this.pendingRequests.rejectAll('Connection closed');
      this.setState('disconnected');
      this.emit('close', event.code, event.reason);

      // Auto-reconnect unless intentionally closed or filtered out
      if (
        !this._intentionalClose &&
        this.options.autoReconnect &&
        this.options.shouldReconnect(event.code, event.reason)
      ) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (event: Event) => {
      this.emit('error', event);
    };
  }

  /** Gracefully disconnect (no auto-reconnect) */
  disconnect(): void {
    this._intentionalClose = true;
    this.cancelReconnect();
    this.keepalive.stop();
    this.pendingRequests.rejectAll('Intentional disconnect');

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }

    this.setState('disconnected');
  }

  /** Mark the connection as authenticated (after successful handshake) */
  setAuthenticated(): void {
    this.setState('authenticated');
  }

  /**
   * Send a request and return a promise for the response payload.
   *
   * The request ID is auto-generated. The response is correlated by ID.
   * Rejects on timeout or error response.
   */
  async request<P, R>(method: string, params: P, timeoutMs?: number): Promise<R> {
    const frame = buildRequest(method, params);
    const promise = this.pendingRequests.add<R>(frame.id, method, timeoutMs);
    this.sendRaw(frame);
    return promise;
  }

  /** Send a raw request frame (for cases where you manage the ID yourself, e.g., connect) */
  sendRaw<P>(frame: RequestFrame<P>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[ws] Cannot send — WebSocket not open');
      return;
    }
    this.ws.send(serializeFrame(frame));
  }

  /**
   * Send a request frame with an explicit ID and return the response promise.
   * Used for the connect handshake where the caller needs to track the ID.
   */
  async sendTracked<P, R>(
    frame: RequestFrame<P>,
    timeoutMs?: number,
  ): Promise<R> {
    const promise = this.pendingRequests.add<R>(frame.id, frame.method, timeoutMs);
    this.sendRaw(frame);
    return promise;
  }

  /** Start keepalive tick interval */
  startKeepalive(intervalMs: number): void {
    this.keepalive.start(intervalMs);
  }

  /** Stop keepalive tick interval */
  stopKeepalive(): void {
    this.keepalive.stop();
  }

  /** Update the connection URL (for reconnection to a different gateway) */
  setUrl(url: string): void {
    this.options.url = url;
  }

  // ── Event Emitter ──

  on<K extends keyof WSClientEvents>(event: K, listener: EventListener<K>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as (...args: unknown[]) => void);
  }

  off<K extends keyof WSClientEvents>(event: K, listener: EventListener<K>): void {
    this.listeners.get(event)?.delete(listener as (...args: unknown[]) => void);
  }

  private emit<K extends keyof WSClientEvents>(
    event: K,
    ...args: Parameters<WSClientEvents[K]>
  ): void {
    const set = this.listeners.get(event);
    if (set) {
      for (const listener of set) {
        try {
          listener(...(args as unknown[]));
        } catch (e) {
          console.error(`[ws] Error in ${event} listener:`, e);
        }
      }
    }
  }

  // ── Internal ──

  private handleMessage(data: string): void {
    const frame = parseFrame(data);
    if (!frame) return;

    if (isResponseFrame(frame)) {
      // Try to match with a pending request
      const matched = this.pendingRequests.resolve(frame);
      if (!matched) {
        this.emit('unmatchedResponse', frame);
      }
      return;
    }

    if (isEventFrame(frame)) {
      // Handle tick events via keepalive manager
      if (frame.event === 'tick') {
        this.keepalive.handleTick();
        return;
      }

      this.emit('event', frame);
    }
  }

  private setState(state: WSClientState): void {
    if (this._state === state) return;
    this._state = state;
    this.emit('stateChange', state);
  }

  private normalizeUrl(url: string): string {
    let ws = url;
    if (ws.startsWith('https://')) ws = ws.replace('https://', 'wss://');
    if (ws.startsWith('http://')) ws = ws.replace('http://', 'ws://');
    if (!ws.startsWith('ws://') && !ws.startsWith('wss://')) ws = 'wss://' + ws;
    return ws;
  }

  private scheduleReconnect(): void {
    this.cancelReconnect();
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, RECONNECT_MAX_DELAY_MS);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
